import { Fragment, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { FaAngleDoubleRight, FaChevronDown, FaInfoCircle, FaSearch, FaThumbsDown, FaThumbsUp, FaTimes } from "react-icons/fa";
import ReservationHeader from "../../Commun/ReservationHeader";
import ReservationSidebar from "../../Commun/ReservationSidebar";
import { useProperty } from "../../../PropertyContext";
import { ResRoomlist } from "../../../Services/Service";
import "../PropertyDetails.css";
import "./BulkUpdate.css";

const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const initialDateRange = () => {
    const from = new Date();
    const to = new Date(from);
    to.setDate(to.getDate() + 1);
    return { from: formatInputDate(from), to: formatInputDate(to) };
};

const DAY_OPTIONS = [
    { value: "mon", label: "MON" },
    { value: "tue", label: "TUE" },
    { value: "wed", label: "WED" },
    { value: "thu", label: "THU" },
    { value: "fri", label: "FRI" },
    { value: "sat", label: "SAT" },
    { value: "sun", label: "SUN" },
];

const formatDisplayDate = (value) => new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
});

const CheckboxMultiSelect = ({ id, label, options, values, onChange, placeholder, searchPlaceholder, selectAllLabel }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef(null);
    const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase()));
    const allSelected = options.length > 0 && options.every((option) => values.includes(option.value));

    useEffect(() => {
        const closeOnOutsideClick = (event) => {
            if (!dropdownRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, []);

    const toggleOption = (value) => {
        onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
    };

    const toggleAll = () => onChange(allSelected ? [] : options.map((option) => option.value));

    return <div className="bulk-checkbox-select" ref={dropdownRef}>
        <label className="form-label" id={`${id}-label`}>{label}</label>
        <div className={`bulk-checkbox-trigger-wrap ${open ? "open" : ""}`}>
            <button type="button" className="bulk-checkbox-trigger" aria-labelledby={`${id}-label`} aria-expanded={open} onClick={() => setOpen((current) => !current)}>
                {values.length ? `${values.length} Selected` : placeholder}
            </button>
            {values.length > 0
                ? <button type="button" className="bulk-checkbox-clear" aria-label={`Clear ${label}`} onClick={() => { onChange([]); setSearch(""); }}>×</button>
                : <span className="bulk-checkbox-caret" aria-hidden="true"><FaChevronDown /></span>}
        </div>

        {open && <div className="bulk-checkbox-menu">
            <div className="bulk-checkbox-search">
                <FaSearch />
                <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} autoFocus />
            </div>
            <label className="bulk-checkbox-option bulk-checkbox-select-all">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                <span>{selectAllLabel}</span>
            </label>
            <div className="bulk-checkbox-options">
                {filteredOptions.map((option) => <label className={`bulk-checkbox-option ${values.includes(option.value) ? "is-checked" : ""}`} key={option.value}>
                    <input type="checkbox" checked={values.includes(option.value)} onChange={() => toggleOption(option.value)} />
                    <span>{option.label}</span>
                </label>)}
                {!filteredOptions.length && <div className="bulk-checkbox-empty">No results found</div>}
            </div>
        </div>}
    </div>;
};

export const BulkUpdate = () => {
    const { selectedProperty } = useProperty();
    const propertyId = selectedProperty?.PropertyID;
    const [step, setStep] = useState(1);
    const [updateType, setUpdateType] = useState("rates");
    const [dateRange, setDateRange] = useState(() => initialDateRange());
    const [rooms, setRooms] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(false);
    const [selectedRoomIds, setSelectedRoomIds] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState(["base-rate"]);
    const [activeRoomIds, setActiveRoomIds] = useState([]);
    const [roomChannelSelections, setRoomChannelSelections] = useState({});
    const [selectedDays, setSelectedDays] = useState(() => DAY_OPTIONS.map((day) => day.value));
    const [roomDaySelections, setRoomDaySelections] = useState({});
    const [roomDayValues, setRoomDayValues] = useState({});
    const [stopSellAction, setStopSellAction] = useState("remove-stop-sell");

    useEffect(() => {
        let active = true;

        if (!propertyId) {
            setRooms([]);
            setSelectedRoomIds([]);
            setActiveRoomIds([]);
            return undefined;
        }

        const fetchRooms = async () => {
            setRoomsLoading(true);
            setSelectedRoomIds([]);
            setActiveRoomIds([]);

            try {
                const data = await ResRoomlist(propertyId);
                if (!active) return;

                const roomList = Array.isArray(data?.RoomsDetails)
                    ? data.RoomsDetails.filter((room) => room?.RoomID && room?.RoomName)
                    : [];
                setRooms(roomList);
            } catch (error) {
                console.error("Bulk update rooms fetch error:", error);
                if (active) setRooms([]);
            } finally {
                if (active) setRoomsLoading(false);
            }
        };

        fetchRooms();

        return () => { active = false; };
    }, [propertyId]);

    const updateDateRange = (field, value) => setDateRange((current) => ({ ...current, [field]: value }));
    const selectedUpdateLabel = {
        rates: "Rates",
        "stop sell": "Stop Sell",
        "stop sell limit": "Stop Sell Limit",
        "min stay arrival": "Min Stay Arrival",
        "max stay arrival": "Max Stay Arrival",
        "min stay through": "Min Stay Through",
        "max stay through": "Max Stay Through",
        "close to arrival": "Close To Arrival",
        "close to departure": "Close To Departure",
    }[updateType];
    const roomOptions = rooms.map((room) => ({ value: String(room.RoomID), label: room.RoomName || "Unnamed Room" }));
    const channelOptions = [
        { value: "base-rate", label: "Base Rate" },
        { value: "booking-com", label: "Booking.com" },
    ];
    const selectedRooms = rooms.filter((room) => selectedRoomIds.includes(String(room.RoomID)));
    const reviewRooms = selectedRooms.filter((room) => activeRoomIds.includes(String(room.RoomID)));
    const selectedChannelOptions = channelOptions.filter((channel) => selectedChannels.includes(channel.value));
    const selectedDayOptions = DAY_OPTIONS.filter((day) => selectedDays.includes(day.value));
    const usesNumericDayInputs = updateType === "min stay arrival";
    const allRoomCardsActive = selectedRoomIds.length > 0 && selectedRoomIds.every((roomId) => activeRoomIds.includes(roomId));

    const changeSelectedRooms = (nextRoomIds) => {
        setSelectedRoomIds(nextRoomIds);
        setActiveRoomIds((current) => [...new Set([...current.filter((roomId) => nextRoomIds.includes(roomId)), ...nextRoomIds])]);
    };

    const toggleAllRoomCards = () => setActiveRoomIds(allRoomCardsActive ? [] : [...selectedRoomIds]);
    const toggleRoomCard = (roomId) => setActiveRoomIds((current) => current.includes(roomId) ? current.filter((id) => id !== roomId) : [...current, roomId]);
    const toggleRoomChannel = (roomId, channelId) => {
        const key = `${roomId}-${channelId}`;
        setRoomChannelSelections((current) => ({ ...current, [key]: current[key] === false }));
    };
    const changeUpdateType = (value) => {
        setUpdateType(value);
        setStep(1);
        if (value === "min stay arrival") setSelectedDays(DAY_OPTIONS.map((day) => day.value));
    };
    const toggleRoomDay = (roomId, dayId) => {
        const key = `${roomId}-${dayId}`;
        setRoomDaySelections((current) => ({ ...current, [key]: current[key] === false }));
    };
    const copyAcrossRoomDays = (roomId) => {
        setRoomDaySelections((current) => {
            const next = { ...current };
            selectedDays.forEach((dayId) => { next[`${roomId}-${dayId}`] = true; });
            return next;
        });
    };
    const changeRoomDayValue = (roomId, dayId, value) => {
        const key = `${roomId}-${dayId}`;
        setRoomDayValues((current) => ({ ...current, [key]: value.replace(/\D/g, "") }));
    };
    const copyAcrossRoomValues = (roomId) => {
        const firstValue = roomDayValues[`${roomId}-${DAY_OPTIONS[0].value}`] || "";
        setRoomDayValues((current) => {
            const next = { ...current };
            DAY_OPTIONS.forEach((day) => { next[`${roomId}-${day.value}`] = firstValue; });
            return next;
        });
    };

    return (
        <div className="wrapper property-details-page bulk-update-page">
            <ReservationSidebar />
            <div className="main-panel">
                <ReservationHeader />
                <div className="container">
                    <div className="page-inner">
                        <div className="card bulk-update-card">
                            <div className="card-header bulk-update-card-header">
                                <div className="bulk-update-title-wrap">
                                    <h4 className="card-title">BULK UPDATE</h4>
                                    <span className="bulk-update-title-divider">/</span>
                                    <select className="form-select bulk-update-type-select" value={updateType} onChange={(event) => changeUpdateType(event.target.value)} aria-label="Bulk update type">
                                        <option value="rates">RATES</option>
                                        <option value="stop sell">STOP SELL</option>
                                        <option value="stop sell limit">STOP SELL LIMIT</option>
                                        <option value="min stay arrival">MIN STAY ARRIVAL</option>
                                        <option value="max stay arrival">MAX STAY ARRIVAL</option>
                                        <option value="min stay through">MIN STAY THROUGH</option>
                                        <option value="max stay through">MAX STAY THROUGH</option>
                                        <option value="close to arrival">CLOSE TO ARRIVAL</option>
                                        <option value="close to departure">CLOSE TO DEPARTURE</option>
                                    </select>
                                </div>

                                <div className="bulk-update-header-actions" aria-label="Page actions">
                                    <button type="button" className="btn btn-link" aria-label="Like"><FaThumbsUp /></button>
                                    <button type="button" className="btn btn-link" aria-label="Dislike"><FaThumbsDown /></button>
                                    <NavLink to="/Reservation/BookingEngine" className="btn btn-link" aria-label="Close bulk update"><FaTimes /></NavLink>
                                </div>
                            </div>

                            <div className="card-body bulk-update-body">
                                <div className="bulk-update-step-row">
                                    <div className="bulk-update-steps" aria-label="Bulk update progress">
                                        <span className={step === 1 ? "active" : ""}>Select Room Type &amp; Date</span>
                                        <FaAngleDoubleRight />
                                        <span className={step === 2 ? "active" : ""}>Update {selectedUpdateLabel}</span>
                                    </div>
                                    {step === 1
                                        ? <button type="button" className="btn btn-secondary bulk-update-next" disabled={!selectedRooms.length} onClick={() => setStep(2)}>Next</button>
                                        : <div className="bulk-update-step-actions">
                                            <button type="button" className="btn btn-secondary bulk-update-previous" onClick={() => setStep(1)}>Previous</button>
                                            <button type="button" className="btn btn-success bulk-update-save">Save</button>
                                        </div>}
                                </div>

                                {updateType === "stop sell" && <fieldset className="bulk-update-stop-sell-options">
                                    <legend className="visually-hidden">Stop sell action</legend>
                                    <div className="form-check form-check-inline">
                                        <input className="form-check-input" type="radio" name="stopSellAction" id="apply-stop-sell" value="apply-stop-sell" checked={stopSellAction === "apply-stop-sell"} onChange={(event) => setStopSellAction(event.target.value)} />
                                        <label className="form-check-label" htmlFor="apply-stop-sell">Apply Stop Sell</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input className="form-check-input" type="radio" name="stopSellAction" id="remove-stop-sell" value="remove-stop-sell" checked={stopSellAction === "remove-stop-sell"} onChange={(event) => setStopSellAction(event.target.value)} />
                                        <label className="form-check-label" htmlFor="remove-stop-sell">Remove Stop Sell</label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input className="form-check-input" type="radio" name="stopSellAction" id="apply-property-stop-sell" value="apply-property-stop-sell" checked={stopSellAction === "apply-property-stop-sell"} onChange={(event) => setStopSellAction(event.target.value)} />
                                        <label className="form-check-label" htmlFor="apply-property-stop-sell">Apply Full Property Stop Sell</label>
                                        <FaInfoCircle className="bulk-update-option-info" title="Apply stop sell to the full property" />
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input className="form-check-input" type="radio" name="stopSellAction" id="remove-property-stop-sell" value="remove-property-stop-sell" checked={stopSellAction === "remove-property-stop-sell"} onChange={(event) => setStopSellAction(event.target.value)} />
                                        <label className="form-check-label" htmlFor="remove-property-stop-sell">Remove Full Property Stop Sell</label>
                                        <FaInfoCircle className="bulk-update-option-info" title="Remove stop sell from the full property" />
                                    </div>
                                </fieldset>}

                                {step === 1 ? <>
                                <div className="bulk-update-date-panel">
                                    <div className="row g-3 align-items-end bulk-update-date-row">
                                        <div className="col-md-4 col-lg-3">
                                            <label className="form-label" htmlFor="bulk-update-from">From</label>
                                            <input id="bulk-update-from" type="date" className="form-control" value={dateRange.from} onChange={(event) => updateDateRange("from", event.target.value)} />
                                        </div>
                                        <div className="col-md-4 col-lg-3">
                                            <label className="form-label" htmlFor="bulk-update-to">To</label>
                                            <input id="bulk-update-to" type="date" className="form-control" value={dateRange.to} min={dateRange.from} onChange={(event) => updateDateRange("to", event.target.value)} />
                                        </div>
                                    </div>
                                </div>

                                {updateType === "rates" && <p className="bulk-update-warning mb-2">
                                    <strong>Note :</strong> Please be aware that rate updated on channels will override the base rates and multipliers setup on mapping. Please use with caution. Contact our support team if you need assistance.
                                </p>}

                                <div className="bulk-update-room-row">
                                    <div className="bulk-update-selector-controls">
                                        <CheckboxMultiSelect id="bulk-update-rooms" label="Rooms" options={roomOptions} values={selectedRoomIds} onChange={changeSelectedRooms} placeholder={roomsLoading ? "Loading Rooms..." : "Select Rooms"} searchPlaceholder="Search Rooms" selectAllLabel="Select All Rooms" />
                                        {selectedRoomIds.length > 0 && <CheckboxMultiSelect id="bulk-update-channels" label="Channels" options={channelOptions} values={selectedChannels} onChange={setSelectedChannels} placeholder="Select Channels" searchPlaceholder="Search Channels" selectAllLabel="Select All Channels" />}
                                    </div>
                                    <button type="button" className="btn bulk-update-search" aria-label="Search room availability"><FaSearch /></button>
                                </div>

                                {selectedRooms.length > 0 && <div className="bulk-update-room-results">
                                    <label className="bulk-update-master-check">
                                        <input type="checkbox" checked={allRoomCardsActive} onChange={toggleAllRoomCards} />
                                        <span>Select All</span>
                                    </label>

                                    <div className="bulk-update-selected-rooms">
                                        {selectedRooms.map((room) => { const roomId = String(room.RoomID); const roomActive = activeRoomIds.includes(roomId); return <div className={`bulk-update-room-card ${roomActive ? "active" : "inactive"}`} key={roomId}>
                                            <div className="bulk-update-room-card-header">
                                                <span className="bulk-update-collapse-mark">−</span>
                                                <input type="checkbox" checked={roomActive} onChange={() => toggleRoomCard(roomId)} aria-label={`Select ${room.RoomName || "room"}`} />
                                                <strong>{room.RoomName || "Unnamed Room"}</strong>
                                            </div>
                                            {selectedChannelOptions.length > 0 && <div className="bulk-update-room-channel-list">
                                                {selectedChannelOptions.map((channel) => { const selectionKey = `${roomId}-${channel.value}`; const channelChecked = roomChannelSelections[selectionKey] !== false; return <div className="bulk-update-room-channel" key={channel.value}>
                                                    <div className="bulk-update-room-channel-name"><span>{channel.label}</span><FaInfoCircle /></div>
                                                    <label>
                                                        <input type="checkbox" checked={channelChecked} disabled={!roomActive} onChange={() => toggleRoomChannel(roomId, channel.value)} />
                                                        <span>{room.RoomName || "Unnamed Room"}</span>
                                                    </label>
                                                </div>; })}
                                            </div>}
                                        </div>; })}
                                    </div>
                                </div>}
                                </> : <div className={`bulk-update-review-step ${usesNumericDayInputs ? "bulk-update-min-stay-step" : ""}`}>
                                    {!usesNumericDayInputs && <div className="bulk-update-custom-days">
                                        <CheckboxMultiSelect id="bulk-update-days" label="Custom Days" options={DAY_OPTIONS} values={selectedDays} onChange={setSelectedDays} placeholder="Select Days" searchPlaceholder="Search Days" selectAllLabel="Select All Days" />
                                    </div>}

                                    <div className="table-responsive bulk-update-review-table-wrap">
                                        <table className="table bulk-update-review-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Rooms</th>
                                                    <th className="text-center">Copy <span aria-hidden="true">⌄</span></th>
                                                    {selectedDayOptions.map((day) => <th className="text-center" key={day.value}>{day.label} <span aria-hidden="true">⌄</span></th>)}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reviewRooms.map((room) => { const roomId = String(room.RoomID); return <Fragment key={roomId}>
                                                    <tr className="bulk-update-review-room-title">
                                                        <td colSpan={selectedDayOptions.length + 2}>{room.RoomName || "Unnamed Room"}</td>
                                                    </tr>
                                                    <tr className="bulk-update-review-room-values">
                                                        <td>{formatDisplayDate(dateRange.from)} - {formatDisplayDate(dateRange.to)}</td>
                                                        <td className="text-center"><button type="button" className="bulk-update-copy-days" onClick={() => usesNumericDayInputs ? copyAcrossRoomValues(roomId) : copyAcrossRoomDays(roomId)} aria-label={`Copy value across ${room.RoomName || "room"}`}><FaAngleDoubleRight /></button></td>
                                                        {selectedDayOptions.map((day) => { const selectionKey = `${roomId}-${day.value}`; const selected = roomDaySelections[selectionKey] !== false; return <td className="text-center" key={day.value}>
                                                            {usesNumericDayInputs
                                                                ? <input type="text" inputMode="numeric" pattern="[0-9]*" className="bulk-update-day-value" value={roomDayValues[selectionKey] || ""} onChange={(event) => changeRoomDayValue(roomId, day.value, event.target.value)} aria-label={`${day.label} minimum stay arrival for ${room.RoomName || "room"}`} />
                                                                : <button type="button" className={`bulk-update-day-toggle ${selected ? "active" : ""}`} aria-label={`${selected ? "Remove" : "Apply"} ${day.label} for ${room.RoomName || "room"}`} onClick={() => toggleRoomDay(roomId, day.value)}><span /></button>}
                                                        </td>; })}
                                                    </tr>
                                                </Fragment>; })}
                                                {!reviewRooms.length && <tr><td className="bulk-update-review-empty" colSpan={selectedDayOptions.length + 2}>No active rooms selected.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
